import LogtoClient, { LogtoConfig, UserScope } from "@logto/browser";
import { runtimeConfig } from "../runtime-config";

// Scopes enum
export enum LogtoScopes {
  AdminAccess = 'admin:access', 
  //internal admin scopes
  AdminOrgCrud = 'admin:orgs:crud',
  AdminOrgRead = 'admin:orgs:read',
  AdminOrgQuotaCrud = 'admin:org:quota:crud',
  AdminOrgQuotaRead = 'admin:org:quota:read',
  AdminOrgUsageRead = 'admin:org:usage:read',
  AdminOrgKeysCrud = 'admin:org:keys:crud',
  AdminOrgKeysRead = 'admin:org:keys:read',
  AdminOrgUsersCrud = 'admin:org:users:crud',
  AdminOrgUsersRead = 'admin:org:users:read',

  DocsRead = 'docs:read',
  GuideWrite = 'guide:crud',
  GuideRead = 'guide:read',
  GuideListUnlisted = 'guide:list:unlisted',

  //external org user scopes
  OrgKeyCrud = 'org:keys:crud',
  OrgKeyRead = 'org:keys:read',
  OrgQuotaRead = 'org:quota:read',
  OrgUsageRead = 'org:usage:read',
  OrgUsersRead = 'org:users:read',
  OrgUsersCrud = 'org:users:crud',
  OrgOwner = 'org:owner', // cannot be removed except by other org org owners.  can also only be promoted by other owners or admins
  OrgShortLivedTokenCreate = 'org:short-lived-token:create',
  McpAccess = 'org:access:mcp',
} 

// Scopes parcel-ui's My Account settings needs on Logto's Account API
// (`/api/my-account`): the profile, email/phone, social identity and MFA panes
// each read or write claims the token simply does not carry unless the client
// asked for them at sign-in.
//
// Mirrors parcel-ui's exported `MyAccountRequiredScopes`, restated here rather
// than imported: that constant only ships from the UI barrel, and this module is
// also loaded by server functions (for `LogtoScopes`), which must not pull the
// component library into the server bundle. parcel-ui's list also names
// `email_verified`, `phone_verified` and `sso_identities` — those are ID token
// claims, not scopes Logto grants, so asking for them buys nothing.
export const MyAccountScopes: string[] = [
  UserScope.Profile,
  UserScope.Email,
  UserScope.Phone,
  UserScope.Address,
  UserScope.CustomData,
  UserScope.Identities,
  UserScope.Roles,
  UserScope.Organizations,
  UserScope.OrganizationRoles,
];

// ── Two different things are called "exchange". They are NOT the same. ──────
//
//   LOGTO ENTITLEMENTS RESOURCE  =  https://entitlements.abodemine.com  ← this one
//
//     The audience a Logto access token must carry to be EXCHANGEABLE for a
//     product token. Entitlements derives it from config/products.yaml key
//     `entitlements` — it is not an env var over there:
//
//         // Entitlements cmd/server/main.go
//         exchangeResource := entitlementsProduct.Audience
//
//     Our env var for it is VITE_LOGTO_ENTITLEMENTS_RESOURCE (previously
//     VITE_EXCHANGE_RESOURCE — renamed because it was too easy to confuse with
//     the EXCHANGE PRODUCT below). Symphony spells the underlying value the
//     same way.
//
//   EXCHANGE PRODUCT   =  https://exchange.abodemine.com         ← NOT this one
//
//     AbodeMine Exchange: loan and asset portfolios, `org:properties:*` and
//     friends. It is an audience you could exchange FOR, never the audience you
//     exchange WITH.
//
// Worked example — the embedded MCP onboarding:
//
//     logtoClient.getAccessToken('https://entitlements.abodemine.com', orgId)
//       -> subject token  (aud: entitlements.abodemine.com, scopeless)
//     POST /oidc/token  grant_type=token-exchange
//                       subject_token=<that>
//                       resource=https://ai.abodemine.com
//       -> Symphony product token
//
// Use the product audience as the subject and the exchange answers:
//     invalid_grant: subject token was not issued for the exchange resource
const LOGTO_ENTITLEMENTS_RESOURCE = runtimeConfig.VITE_LOGTO_ENTITLEMENTS_RESOURCE

// Function to create Logto configuration from runtime config
export function createLogtoConfig(runtimeConfig: {
  endpoint: string
  appId: string
  resource: string
}): LogtoConfig {
  return {
    endpoint: runtimeConfig.endpoint,
    appId: runtimeConfig.appId,
    // Both declared up front: Logto grants what the authorization request asked
    // for, so a getAccessToken() for a resource that was never requested has no
    // grant to draw on. See LOGTO_ENTITLEMENTS_RESOURCE above for what the second one is.
    resources: [runtimeConfig.resource, LOGTO_ENTITLEMENTS_RESOURCE].filter(Boolean),
    scopes: [
      ...new Set([
        'openid',
        'offline_access',
        ...MyAccountScopes,
        ...Object.values(LogtoScopes),
      ]),
    ],
  }
}

// Function to create Logto client with runtime config
export function createLogtoClient(customConfig?: {
  endpoint: string
  appId: string
  resource: string
}): LogtoClient {
  // Use provided config or get from runtime config
  const config = customConfig ? createLogtoConfig(customConfig) : createLogtoConfig({
    endpoint: runtimeConfig.VITE_LOGTO_ENDPOINT,
    appId: runtimeConfig.VITE_LOGTO_APPID,
    resource: runtimeConfig.VITE_LOGTO_RESOURCE
  })
  return new LogtoClient(config)
}

// Legacy exports for backward compatibility (deprecated)
// These will be removed once all usage is migrated to runtime config
export const logtoConfig: LogtoConfig = {
  endpoint: runtimeConfig.VITE_LOGTO_ENDPOINT,
  appId: runtimeConfig.VITE_LOGTO_APPID,
  resources: [runtimeConfig.VITE_LOGTO_RESOURCE, LOGTO_ENTITLEMENTS_RESOURCE].filter(Boolean),
  scopes: [
    ...new Set([
      'openid',
      'offline_access',
      ...MyAccountScopes,
      ...Object.values(LogtoScopes),
    ]),
  ],
}

export const logtoClient = createLogtoClient();

